import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  IoCloseOutline,
  IoDownloadOutline,
  IoCheckmarkDoneOutline,
} from 'react-icons/io5';
import { MdOutlineTableChart } from 'react-icons/md';
import * as XLSX from 'xlsx';
import AppDatatable from '../../../components/AppDatatable';
import AccessDeniedState from '../../../components/AccessDeniedState';
import OpenFormDatePicker from '../../../components/form/OpenFormDatePicker';
import RichTextEditor, { RichTextContent } from '../../../components/form/RichTextEditor';
import { readLocalCollection } from '../../../utils/localData';
import { getLoggedInUser, getModulePermissions, hasModulePermission } from '../../../utils/permissions';
import useRecordLock from '../../../hooks/useRecordLock';
import { getDateRangeParams, getDefaultWeekRange } from '../../../utils/dateRange';
import { openDocumentViewer } from '../../../utils/documentViewer';

const VEHICLE_FIELDS = [
  { key: 'ra_number', label: 'R/A Number', type: 'text' },
  { key: 'unit', label: 'Unit', type: 'text' },
  { key: 'license_plate', label: 'License Plate Number', type: 'text' },
  { key: 'model_colour', label: 'Model & Colour', type: 'text' },
  { key: 'year_make', label: 'Year & Make', type: 'text' },
  { key: 'vin', label: 'VIN', type: 'text' },
];

const FINANCIAL_FIELDS = [
  { key: 'repaired_amount', label: 'Repaired Amount', type: 'number' },
  { key: 'charged_received_amount', label: 'Charged/Received Amount', type: 'number' },
  { key: 'lou', label: 'LOU', type: 'number' },
  { key: 'admin_fee', label: 'Admin Fee', type: 'number' },
  { key: 'tow_check', label: 'Tow Check', type: 'number' },
  { key: 'salvage_amount', label: 'Salvage Amount', type: 'number' },
  { key: 'sold', label: 'Sold', type: 'number' },
  { key: 'storage_charge', label: 'Storage Charge', type: 'number' },
  { key: 'amount_due_customer', label: 'Amount Due from Customer', type: 'number' },
  { key: 'current_cost', label: 'Current Cost', type: 'number' },
  { key: 'acv', label: 'ACV', type: 'number' },
];

const PAYMENT_FIELDS = [
  { key: 'chargeback', label: 'Chargeback', type: 'select', options: ['Yes', 'No'] },
  { key: 'difference', label: 'Difference', type: 'number' },
  { key: 'charged_customer', label: 'Charged Customer', type: 'number' },
  { key: 'check_received', label: 'Check Received', type: 'number' },
  { key: 'cash', label: 'Cash', type: 'number' },
  { key: 'wire_transfer', label: 'Wire Transfer', type: 'number' },
];

const OTHER_FIELDS = [
  { key: 'internal_notes', label: 'Internal Notes', type: 'textarea' },
  { key: 'repaired_at', label: 'Repaired AT', type: 'text' },
];

const PROCESSING_GROUPS = [
  { title: 'Claim Assessment', fields: [
    { key: 'claim_status', label: 'Claim Status', type: 'text', readOnly: true },
    { key: 'insurance_route', label: 'Insurance Opted For', type: 'select', options: ['Routes CDW', 'Personal Insurance', 'Credit Card Insurance', 'OTA'] },
    { key: 'appraisal_requested_date', label: 'Appraisal Requested Date', type: 'date' },
    { key: 'estimate_requested_date', label: 'Estimate Requested Date', type: 'date' },
    { key: 'insurance_claim_number', label: 'Claim #', type: 'text' },
    { key: 'adjuster_name', label: 'Adjuster Name', type: 'text' },
    { key: 'adjuster_phone', label: 'Adjuster Phone', type: 'text' },
    { key: 'adjuster_email', label: 'Adjuster Email Address', type: 'email' },
  ] },
  { title: 'Fault, Liability & Payer', fields: [
    { key: 'party_at_fault', label: 'Party at Fault', type: 'select', options: ['Renter', '3rd Party'] },
    { key: 'liability_accepted_by_insurance', label: 'Liability Accepted by Insurance', type: 'select', options: ["Renter's Ins", "3rd Party's Personal Ins"] },
    { key: 'renter_insurance_type', label: "Renter's Insurance Type", type: 'select', options: ['Personal', 'OTA', 'Credit card'] },
    { key: 'payer', label: 'Who is paying us?', type: 'select', options: ['Renter', "Renter's Ins", '3rd Party', "3rd Party's Ins"] },
    { key: 'payer_charged', label: 'Charged', type: 'select', options: ['Yes', 'No'] },
    { key: 'payer_charged_amount', label: 'Amount Charged', type: 'number' },
  ] },
  { title: 'Repairs & Settlement', fields: [
    { key: 'repairs_approved_at', label: 'Repairs Approved At', type: 'select', options: ['Body Shop', 'In-house', 'Not repairing due to minor damage'] },
    { key: 'body_shop_name', label: 'Body Shop Name', type: 'text' },
    { key: 'amount_approved', label: 'Amount Approved', type: 'number' },
    { key: 'parts_cost_included', label: 'Parts Cost Included', type: 'number' },
    { key: 'vehicle_location', label: 'Vehicle Location', type: 'select', options: ['At location - not yet sent to body shop', 'At body shop getting repaired', 'Repaired and returned to location'] },
    { key: 'original_demand_amount', label: 'Original Demand Amount', type: 'number' },
    { key: 'settlement_offer_amount', label: 'Settlement Offer Amount', type: 'number' },
    { key: 'settlement_decision', label: 'Settlement Decision', type: 'select', options: ['Pending', 'Accepted', 'Negotiated'] },
    { key: 'team_lead_approval_status', label: 'Team Lead Approval', type: 'select', options: ['Pending', 'Approved'] },
    { key: 'approved_by', label: 'Approved By', type: 'text' },
  ] },
  { title: 'Follow Up', fields: [
    { key: 'follow_up_date', label: 'New Follow-up Date', type: 'date' },
    { key: 'follow_up_notes', label: 'New Follow-up Notes', type: 'textarea' },
  ] },
  { title: 'Deductible & Payment', fields: [
    { key: 'deductible_applied', label: 'Deductible Applied', type: 'select', options: ['Yes', 'No'] },
    { key: 'deductible_applied_amount', label: 'Deductible Amount', type: 'number' },
    { key: 'charged_deductible', label: 'Charged Deductible', type: 'select', options: ['Yes', 'No'] },
    { key: 'mode_of_payment', label: 'Mode of Payment', type: 'select', options: ['Charged Renter', '3rd Party', 'Bank/Wire Transfer', 'Credit Card', 'Check1', 'Check2', 'None'] },
    { key: 'payment_amount', label: 'Payment Amount', type: 'number' },
    { key: 'check1_received_date', label: 'Check 1 Received Date', type: 'date' },
    { key: 'check1_amount', label: 'Check 1 Amount', type: 'number' },
    { key: 'check1_received_insurance_company', label: 'Check 1 Received Insurance Company', type: 'text' },
    { key: 'check2_received_date', label: 'Check 2 Received Date', type: 'date' },
    { key: 'check2_amount', label: 'Check 2 Amount', type: 'number' },
    { key: 'check2_received_insurance_company', label: 'Check 2 Received Insurance Company', type: 'text' },
  ] },
  { title: 'Collections', fields: [
    { key: 'collections_reason', label: 'Reason', type: 'select', options: ['Customer not responding', 'Ins denied claim'] },
    { key: 'collections_amount', label: 'Amount', type: 'number' },
    { key: 'collections_date_added', label: 'Date Added', type: 'date' },
  ] },
];

const DEMAND_PACKAGE_ITEMS = [
  ['demandLetter', 'Demand Letter'], ['signedRentalAgreement', 'Signed R/A'],
  ['damagePhotos', 'Damage Photos'], ['damageIncidentReport', 'Damage/Incident Report'],
  ['aob', 'AOB'], ['towImpoundBill', 'Tow Bill / Impound Bill'],
  ['repairEstimateAppraisalAcv', 'Repair Estimate / Appraisal / ACV'],
  ['salvage', 'Salvage'], ['closedRentalAgreement', 'Closed R/A'],
];

const FIELDS = [...VEHICLE_FIELDS, ...FINANCIAL_FIELDS, ...PAYMENT_FIELDS, ...OTHER_FIELDS];

const CURRENCY_KEYS = [
  'repaired_amount', 'charged_received_amount', 'lou', 'admin_fee', 'tow_check',
  'salvage_amount', 'sold', 'storage_charge', 'amount_due_customer', 'current_cost',
  'acv', 'difference', 'charged_customer', 'check_received', 'cash', 'wire_transfer',
];

const TABLE_ONLY_FIELDS_FIRST = [
  { key: 'insurance_number', label: 'Insurance Number' },
  { key: 'location_name', label: 'Location Name' },
  { key: 'loss_date', label: 'Date of Loss' },
];
const TABLE_ONLY_FIELDS_LAST = [
  { key: 'created_by', label: 'Created Name' },
  { key: 'created_datetime', label: 'Created Date' },
];
const CLOSING_STAGE_KEYS = [...FINANCIAL_FIELDS, ...PAYMENT_FIELDS, ...OTHER_FIELDS].map((field) => field.key);
const emptyForm = () => Object.fromEntries(FIELDS.map((field) => [field.key, '']));
const CLOSED_FORM_PERMISSION_KEYS = ['Close Form', 'Closed Form', 'Closed Forms', 'Close Report'];

const hasValue = (value) => String(value ?? '').trim() !== '';
const getFirstVehicle = (row = {}) => (
  Array.isArray(row.details_by_vehicle) && row.details_by_vehicle.length
    ? row.details_by_vehicle[0]
    : {}
);
const getClaimsProcessComments = (row = {}) => {
  const entries = row.internal_note_entries ?? row.processing_snapshot?.internal_note_entries;
  return Array.isArray(entries) ? entries : [];
};

const createClosingFormFromRow = (row = {}) => {
  const vehicle = getFirstVehicle(row);

  return {
    ...emptyForm(),
    ...row,
    check1_received_date: row.check1_received_date || (['Check', 'Check1'].includes(row.mode_of_payment) ? row.check_received_date : ''),
    check1_amount: row.check1_amount || (['Check', 'Check1'].includes(row.mode_of_payment) ? row.check_amount : ''),
    check1_received_insurance_company: row.check1_received_insurance_company || (['Check', 'Check1'].includes(row.mode_of_payment) ? row.check_received_insurance_company : ''),
    check2_received_date: row.check2_received_date || (row.mode_of_payment === 'Check2' ? row.check_received_date : ''),
    check2_amount: row.check2_amount || (row.mode_of_payment === 'Check2' ? row.check_amount : ''),
    check2_received_insurance_company: row.check2_received_insurance_company || (row.mode_of_payment === 'Check2' ? row.check_received_insurance_company : ''),
    location_name: row.branchLocationCode || row.location_name || '',
    ra_number: row.rentalAgreementNumber || row.ra_number || '',
    unit: vehicle.unitFleetNumber || row.unit || '',
    license_plate: vehicle.licensePlate || vehicle.plateNumber || row.license_plate || row.licensePlate || row.plateNumber || '',
    model_colour: row.model_colour || [vehicle.model, vehicle.color].filter(Boolean).join('*'),
    year_make: row.year_make || [vehicle.year, vehicle.make].filter(Boolean).join(' '),
    vin: vehicle.vin || row.vin || '',
    internal_note_entries: getClaimsProcessComments(row),
    new_internal_note_entry: { note: '' },
  };
};

const getRecordStatus = (row) => {
  if (row?.claim_workflow_stage === 'closing' && !row?.closure_completed && row?.claim_status !== 'Completed') return 'Open';
  if (row?.closure_completed || row?.claim_status === 'Completed') return 'Closed';
  const closingFieldCount = CLOSING_STAGE_KEYS.filter((key) => hasValue(row?.[key])).length;
  if (closingFieldCount === 0) return 'Open';
  return 'Closed';
};

const parseDateFilterValue = (value, endOfDay = false) => {
  if (!value) return null;
  const parsed = new Date(`${value}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
};

const parseRecordDateValue = (value) => {
  if (!value) return null;

  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed.getTime();

  const match = String(value).trim().match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (!match) return null;

  const [, day, month, year] = match;
  const legacyDate = new Date(Number(year), Number(month) - 1, Number(day));
  if (
    legacyDate.getFullYear() !== Number(year)
    || legacyDate.getMonth() !== Number(month) - 1
    || legacyDate.getDate() !== Number(day)
  ) return null;

  return legacyDate.getTime();
};

const isWithinDateRange = (value, fromDate, toDate) => {
  if (!fromDate && !toDate) return true;

  const recordTime = parseRecordDateValue(value);
  if (recordTime === null) return false;
  const fromTime = parseDateFilterValue(fromDate);
  const toTime = parseDateFilterValue(toDate, true);

  return (!fromTime || recordTime >= fromTime) && (!toTime || recordTime <= toTime);
};

const getClosedRecordDate = (row = {}) => (
  row.closed_datetime || row.updated_datetime || row.created_datetime || null
);

const isInvalidDateRange = (fromDate, toDate) => {
  if (!fromDate || !toDate) return false;
  const fromTime = parseDateFilterValue(fromDate);
  const toTime = parseDateFilterValue(toDate, true);
  return Boolean(fromTime && toTime && fromTime > toTime);
};

const historyActionLabel = (action = '') => ({
  create: 'Claim Created',
  update: 'Record Updated',
  move_to_claim_processing: 'Moved to Claims Processing',
  move_back_to_claim_processing: 'Moved Back to Claims Processing',
  claim_processing_update: 'Claims Processing Updated',
  move_to_closing: 'Moved to Closed Forms',
  close: 'Claim Closed',
  delete: 'Record Deleted',
}[String(action).toLowerCase()] || String(action || 'Activity').replaceAll('_', ' '));

const changedHistoryFields = (log = {}) => {
  const before = log.before_data && typeof log.before_data === 'object' ? log.before_data : {};
  const after = log.after_data && typeof log.after_data === 'object' ? log.after_data : {};
  const ignored = new Set(['_id', 'created_datetime', 'updated_datetime', 'created_ip', 'updated_ip']);
  return [...new Set([...Object.keys(before), ...Object.keys(after)])]
    .filter((key) => !ignored.has(key) && JSON.stringify(before[key]) !== JSON.stringify(after[key]));
};

const formatHistoryValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const buildUploadedFileUrl = (baseURL, file = {}, download = false) => {
  const normalizedBaseURL = String(baseURL || '').replace(/\/$/, '');
  const normalizedPath = String(file.path || file.saved_name || '').replace(/\\/g, '/');
  const fileName = normalizedPath.split('/').filter(Boolean).pop();
  if (!normalizedBaseURL || !fileName) return '';
  const url = `${normalizedBaseURL}/open-form-upload/${encodeURIComponent(fileName)}`;
  return download ? `${url}?download=1` : url;
};

const viewAllUploadedFiles = (baseURL, files = []) => {
  openDocumentViewer(files.map((file, index) => ({
    name: String(file?.original_name || file?.saved_name || `Document ${index + 1}`).replace(/[<>&"']/g, ''),
    url: buildUploadedFileUrl(baseURL, file),
    contentType: file?.content_type,
  })));
};

export default function ClosedForm({
  closedFetchEndpoint = '/get_closed_forms',
  openFetchEndpoint = '/get_claim_processing_forms',
  updateEndpointBase = '/update_closed_form',
  closeEndpointBase = '/close_submitted_open_form',
  deleteEndpointBase = '/delete_closed_form',
  moveBackEndpointBase = '/move_closed_form_to_claim_processing',
  historyEndpointBases = ['/open_form_event_logs', '/claim_processing_event_logs', '/closed_form_event_logs'],
  permissionKeys = CLOSED_FORM_PERMISSION_KEYS,
  title = 'Close Form',
  uploadWorkflowCategory = 'shuttle-bus',
}) {
  const [rows, setRows] = useState([]);
  const [openRows, setOpenRows] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [selectedNoteRow, setSelectedNoteRow] = useState(null);
  const [selectedCloseRecordId, setSelectedCloseRecordId] = useState('');
  const [notesValue, setNotesValue] = useState('');
  const [notesLoading, setNotesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notesSubmitting, setNotesSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [notesError, setNotesError] = useState('');
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('All Locations');
  const [fromDate, setFromDate] = useState(() => getDefaultWeekRange().fromDate);
  const [toDate, setToDate] = useState(() => getDefaultWeekRange().toDate);
  const [validationErrors, setValidationErrors] = useState({});
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [editingClosedId, setEditingClosedId] = useState('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyRecord, setHistoryRecord] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [selectedHistoryLog, setSelectedHistoryLog] = useState(null);
  const [documentsUpdating, setDocumentsUpdating] = useState(false);
  const [movingBackId, setMovingBackId] = useState(null);

  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const currentUser = getLoggedInUser();
  const { acquireRecordLock, releaseRecordLock } = useRecordLock({ baseURL, userName: currentUser?.userName });
  const accessRole = currentUser?.accessRole || {};
  const closedFormPermissions = getModulePermissions(permissionKeys, accessRole);
  const canView = hasModulePermission(permissionKeys, 'View', accessRole);
  const canEdit = closedFormPermissions.Edit === true;
  const canEditDocuments = canEdit || closedFormPermissions['Document Edit'] === true;
  const canDelete = closedFormPermissions.Delete === true;
  const dateRangeError = isInvalidDateRange(fromDate, toDate);

  const fetchData = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${baseURL}${closedFetchEndpoint}`, { params: getDateRangeParams(fromDate, toDate) }),
      axios.get(`${baseURL}${openFetchEndpoint}`, { params: getDateRangeParams(fromDate, toDate) }),
    ])
      .then(([closedResponse, openResponse]) => {
        setRows(Array.isArray(closedResponse.data) ? closedResponse.data : []);
        setOpenRows(Array.isArray(openResponse.data) ? openResponse.data : []);
      })
      .catch(() => setError('Failed to load records.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    axios.get(`${baseURL}/get_locations`)
      .then((res) => {
        const apiLocations = Array.isArray(res.data) ? res.data : [];
        const localLocations = readLocalCollection('ra_locations_local');
        const merged = [...apiLocations];

        localLocations.forEach((location) => {
          if (!merged.some((entry) => String(entry?._id || entry?.location_name) === String(location?._id || location?.location_name))) {
            merged.push(location);
          }
        });

        setLocations(merged);
      })
      .catch(() => setLocations(readLocalCollection('ra_locations_local')));
  }, [baseURL]);

  useEffect(() => {
    fetchData();
  }, [baseURL, closedFetchEndpoint, fromDate, openFetchEndpoint, toDate]);

  const locationOptions = useMemo(() => {
    const apiLocations = locations.map((loc) => loc?.location_name).filter(Boolean);
    const rowLocations = [...rows, ...openRows].map((row) => row?.branchLocationCode || row?.location_name).filter(Boolean);
    return ['All Locations', ...new Set([...apiLocations, ...rowLocations])];
  }, [locations, openRows, rows]);

  const openRecords = useMemo(
    () => {
      const byId = new Map();
      [...openRows, ...rows].forEach((row) => {
        if (getRecordStatus(row) === 'Open') {
          byId.set(String(row._id), row);
        }
      });
      return Array.from(byId.values());
    },
    [openRows, rows]
  );

  const locationFilteredRows = useMemo(() => {
    const filteredByLocation = selectedLocation === 'All Locations'
      ? rows
      : rows.filter((row) => (row.branchLocationCode || row.location_name) === selectedLocation);

    if (dateRangeError) return [];

    return filteredByLocation.filter((row) => isWithinDateRange(getClosedRecordDate(row), fromDate, toDate));
  }, [dateRangeError, fromDate, rows, selectedLocation, toDate]);

  const formGroups = useMemo(() => ([
    { title: 'Financials', fields: FINANCIAL_FIELDS },
    { title: 'Payments', fields: PAYMENT_FIELDS },
    { title: 'Other', fields: OTHER_FIELDS },
  ]), []);

  const handleLocationChange = (e) => {
    setSelectedLocation(e.target.value);
  };

  const openCloseFormModal = async (row = null) => {
    if (!canEdit) return;

    const record = row ?? openRecords[0] ?? null;
    if (!record?._id) return;
    const acquired = await acquireRecordLock(record._id, 'Close Existing Form');
    if (!acquired) return;

    setSelectedCloseRecordId(record?._id ?? '');
    setEditingClosedId('');
    setForm(record ? createClosingFormFromRow(record) : emptyForm());
    setError('');
    setSuccessMessage('');
    setValidationErrors({});
    setShowModal(true);
  };

  const openEditClosedModal = async (row) => {
    if (!canEditDocuments) return;
    const acquired = await acquireRecordLock(row._id, 'Edit Closed Form');
    if (!acquired) return;
    setEditingClosedId(String(row._id));
    setSelectedCloseRecordId(String(row._id));
    setForm(createClosingFormFromRow(row));
    setError('');
    setSuccessMessage('');
    setValidationErrors({});
    setShowModal(true);
  };

  const resetFormState = () => {
    releaseRecordLock();
    setForm(emptyForm());
    setSelectedCloseRecordId('');
    setEditingClosedId('');
    setError('');
    setSuccessMessage('');
    setValidationErrors({});
    setShowModal(false);
  };

  const resetNotesState = () => {
    setShowNotesModal(false);
    setSelectedNoteRow(null);
    setNotesValue('');
    setNotesLoading(false);
    setNotesError('');
    setNotesSubmitting(false);
  };

  const handleOpenHistory = (row) => {
    setHistoryRecord(row);
    setHistoryRows([]);
    setHistoryError('');
    setHistoryLoading(true);
    setShowHistoryModal(true);

    Promise.all(historyEndpointBases.map((endpoint) => axios.get(`${baseURL}${endpoint}/${row._id}`)))
      .then((responses) => {
        const logs = responses.flatMap((response) => Array.isArray(response?.data?.logs) ? response.data.logs : []);
        const uniqueLogs = Array.from(new Map(logs.map((log) => [
          `${log._id}-${log.action}-${log.event_datetime}`,
          log,
        ])).values());
        uniqueLogs.sort((a, b) => new Date(b.event_datetime || 0) - new Date(a.event_datetime || 0));
        setHistoryRows(uniqueLogs);
      })
      .catch(() => setHistoryError('Unable to load the complete event log for this record.'))
      .finally(() => setHistoryLoading(false));
  };

  const handleCloseHistory = () => {
    setShowHistoryModal(false);
    setHistoryRecord(null);
    setHistoryRows([]);
    setHistoryError('');
    setSelectedHistoryLog(null);
  };

  const updateClosedFormDocuments = async (retainedFiles, newFiles = []) => {
    if (!canEditDocuments) {
      setError('You do not have permission to edit claim documents.');
      return;
    }
    if (!selectedCloseRecordId) return;
    setDocumentsUpdating(true);
    setError('');
    const formData = new FormData();
    formData.append('retainedUploadedFiles', JSON.stringify(retainedFiles));
    formData.append('updated_by', currentUser?.userName || '');
    Array.from(newFiles).forEach((file) => formData.append('uploadedFiles', file));
    try {
      const response = await axios.post(`${baseURL}/claim-uploads/${uploadWorkflowCategory}/${selectedCloseRecordId}`, formData);
      const uploadedFiles = Array.isArray(response.data?.uploaded_files) ? response.data.uploaded_files : [];
      setForm((previous) => ({ ...previous, uploaded_files: uploadedFiles }));
      setRows((previous) => previous.map((row) => (
        String(row._id) === String(selectedCloseRecordId) ? { ...row, uploaded_files: uploadedFiles } : row
      )));
    } catch (documentError) {
      setError('Unable to update uploaded documents right now.');
    } finally {
      setDocumentsUpdating(false);
    }
  };

  const handleDeleteClosedFormDocument = async (fileToDelete) => {
    const confirmation = await Swal.fire({
      icon: 'warning',
      title: 'Delete uploaded document?',
      text: 'This document will be removed from the claim.',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d32f2f',
    });
    if (!confirmation.isConfirmed) return;
    const retainedFiles = (form.uploaded_files || []).filter((file) => file !== fileToDelete);
    updateClosedFormDocuments(retainedFiles);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({ ...prev, [name]: value }));
    setSuccessMessage('');
    setValidationErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleDemandPackageChange = (key) => {
    setForm((prev) => ({
      ...prev,
      demand_package: {
        ...prev.demand_package,
        [key]: !prev.demand_package?.[key],
      },
    }));
  };

  const handleCloseRecordSelection = async (e) => {
    const nextId = e.target.value;
    const selectedRow = openRecords.find((row) => String(row._id) === nextId);

    if (!selectedRow) {
      releaseRecordLock();
    } else {
      const acquired = await acquireRecordLock(selectedRow._id, 'Close Existing Form');
      if (!acquired) return;
    }

    setSelectedCloseRecordId(nextId);
    setForm(selectedRow ? createClosingFormFromRow(selectedRow) : emptyForm());
    setError('');
    setValidationErrors({});
  };

  const buildPayload = () => {
    return {
      ...form,
      closed_by: currentUser?.userName,
      updated_by: currentUser?.userName,
    };
  };

  const handleSubmit = () => {
    if (!canEdit) {
      setError('You do not have permission to close forms.');
      return;
    }

    const nextValidationErrors = ['location_name', 'ra_number'].reduce((acc, key) => {
      if (!String(form[key] ?? '').trim()) {
        acc[key] = true;
      }
      return acc;
    }, {});

    if (!selectedCloseRecordId) {
      setError('Please select an open form before entering the remaining closing details.');
      return;
    }

    if (Object.keys(nextValidationErrors).length > 0) {
      setValidationErrors(nextValidationErrors);
      setError('Please select an open form and fill in the required closing details.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccessMessage('');

    const wasEditing = Boolean(editingClosedId);
    const endpoint = wasEditing
      ? `${baseURL}${updateEndpointBase}/${editingClosedId}`
      : `${baseURL}${closeEndpointBase}/${selectedCloseRecordId}`;

    axios.post(endpoint, buildPayload())
      .then(() => {
        resetFormState();
        fetchData();
        setSuccessMessage(wasEditing ? 'Closed form updated successfully.' : 'Submitted claim closed successfully.');
      })
      .catch(() => {
        setError('Failed to save the closing details. Please try again.');
      })
      .finally(() => setSubmitting(false));
  };

  const formatCell = (key, val) => {
    if (val === '' || val === null || val === undefined) return '-';
    if (CURRENCY_KEYS.includes(key)) return `$${Number(val).toLocaleString()}`;
    return val;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';

    const timestamp = parseRecordDateValue(dateString);
    if (timestamp === null) return String(dateString);
    const date = new Date(timestamp);
    const pad = (n) => n.toString().padStart(2, '0');
    const day = pad(date.getUTCDate());
    const month = pad(date.getUTCMonth() + 1);
    const year = date.getUTCFullYear();

    let hours = date.getUTCHours();
    const minutes = pad(date.getUTCMinutes());
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;

    return `${day}-${month}-${year} ${pad(hours)}:${minutes} ${ampm} UTC`;
  };
  const formatLossDate = (row) => {
    const value = row.accidentDate || row.date_of_loss || row.accidentDateTime
      || row.reported_claim?.accidentDate || row.reported_claim?.date_of_loss
      || row.reported_claim?.accidentDateTime;
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? String(value)
      : date.toLocaleDateString('en-CA');
  };

  const handleExportExcel = () => {
    const exportFields = [
      { key: 'sl_no', label: 'Sl.No' },
      { key: 'record_status', label: 'Form Status' },
      ...TABLE_ONLY_FIELDS_FIRST,
      ...FIELDS,
      ...TABLE_ONLY_FIELDS_LAST,
    ];

    const headerRow = exportFields.map((field) => field.label);
    const bodyRows = locationFilteredRows.map((row, index) => (
      exportFields.map((field) => {
        if (field.key === 'sl_no') return index + 1;
        if (field.key === 'record_status') return getRecordStatus(row);
        if (field.key === 'created_datetime') return formatDate(row.created_datetime);
        if (field.key === 'location_name') return row.branchLocationCode || row.location_name || '';
        if (field.key === 'ra_number') return row.rentalAgreementNumber || row.ra_number || '';
        if (field.key === 'unit') return getFirstVehicle(row).unitFleetNumber || row.unit || '';
        if (field.key === 'license_plate') return getFirstVehicle(row).licensePlate || getFirstVehicle(row).plateNumber || row.license_plate || row.licensePlate || row.plateNumber || '';
        if (field.key === 'model_colour') return row.model_colour || [getFirstVehicle(row).model, getFirstVehicle(row).color].filter(Boolean).join('*');
        if (field.key === 'year_make') return row.year_make || [getFirstVehicle(row).year, getFirstVehicle(row).make].filter(Boolean).join(' ');
        if (field.key === 'vin') return getFirstVehicle(row).vin || row.vin || '';
        if (field.key === 'chargeback') return row[field.key] || '';
        return row[field.key] ?? '';
      })
    ));

    const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...bodyRows]);
    worksheet['!cols'] = exportFields.map((field) => {
      if (field.key === 'internal_notes') return { wch: 28 };
      if (field.key === 'created_datetime') return { wch: 24 };
      if (field.key === 'record_status') return { wch: 14 };
      if (field.key === 'location_name') return { wch: 20 };
      return { wch: Math.max(field.label.length + 4, 14) };
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Insurance Records');

    const safeLocation = selectedLocation.replace(/[\\/:*?"<>|]/g, '_');
    XLSX.writeFile(workbook, `insurance-records-${safeLocation.toLowerCase().replace(/\s+/g, '-')}.xlsx`);
  };

  const fetchInsuranceNotes = async (insuranceNumber) => {
    const response = await axios.get(`${baseURL}/records_notes`, {
      params: { insurance_number: insuranceNumber },
    });

    const payload = response?.data;

    if (Array.isArray(payload)) {
      return payload[0]?.notes ?? payload[0]?.internal_notes ?? '';
    }

    return payload?.notes ?? payload?.internal_notes ?? '';
  };

  const handleOpenNotesModal = async (row) => {
    if (!canEdit) {
      setNotesError('You do not have permission to update notes.');
      setShowNotesModal(true);
      return;
    }

    setSelectedNoteRow(row);
    setNotesValue('');
    setNotesError('');
    setShowNotesModal(true);
    setNotesLoading(true);

    try {
      const fetchedNotes = await fetchInsuranceNotes(row.insurance_number);
      setNotesValue(fetchedNotes);
    } catch {
      setNotesError('Failed to load notes. Please try again.');
    } finally {
      setNotesLoading(false);
    }
  };

  const handleSubmitNotes = () => {
    if (!selectedNoteRow) return;
    if (!canEdit) {
      setNotesError('You do not have permission to update notes.');
      return;
    }

    setNotesSubmitting(true);
    setNotesError('');

    const payload = {
      notes: notesValue,
      insurance_number: selectedNoteRow.insurance_number,
      created_by: selectedNoteRow.created_by || currentUser?.userName,
    };

    axios.post(`${baseURL}/records_submit_notes`, payload)
      .then(() => {
        resetNotesState();
        fetchData();
      })
      .catch(() => setNotesError('Failed to save notes. Please try again.'))
      .finally(() => setNotesSubmitting(false));
  };

  const handleMoveBackToProcessing = async (row) => {
    const confirmation = await Swal.fire({
      icon: 'question',
      title: 'Move back to Claims Processing?',
      text: 'This form will be removed from Closed Forms and returned to the Claims Processing tab.',
      showCancelButton: true,
      confirmButtonText: 'Move Back',
      cancelButtonText: 'Cancel',
    });
    if (!confirmation.isConfirmed) return;

    setMovingBackId(row._id);
    setError('');
    axios.post(`${baseURL}${moveBackEndpointBase}/${row._id}`, {
      updated_by: currentUser?.userName,
    })
      .then(() => {
        setRows((previous) => previous.filter((item) => String(item._id) !== String(row._id)));
        setSuccessMessage('The form has been moved back to Claims Processing.');
        fetchData();
      })
      .catch(() => setError('Failed to move the form back to Claims Processing.'))
      .finally(() => setMovingBackId(null));
  };

  const confirmDelete = () => {
    if (!canDelete) {
      setError('You do not have permission to delete records.');
      return;
    }

    axios.delete(`${baseURL}${deleteEndpointBase}/${deleteId}`, {
      data: { deleted_by: currentUser?.userName },
    })
      .then(() => {
        fetchData();
        setShowDeleteModal(false);
        setDeleteId(null);
      })
      .catch(() => setError('Failed to delete record.'));
  };

  const renderField = (field) => {
    if (field.type === 'select') {
      return (
        <select
          className={validationErrors[field.key] ? 'ra-input-error' : ''}
          name={field.key}
          value={form[field.key] ?? ''}
          onChange={handleChange}
        >
          <option value="">Select...</option>
          {field.key === 'mode_of_payment' && form[field.key] === 'Check' && (
            <option value="Check">Check</option>
          )}
          {field.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === 'textarea') {
      return (
        <textarea
          className={validationErrors[field.key] ? 'ra-input-error' : ''}
          name={field.key}
          value={form[field.key] ?? ''}
          onChange={handleChange}
          readOnly={Boolean(field.readOnly)}
          placeholder={`Enter ${field.label.toLowerCase()}...`}
        />
      );
    }

    return (
      <input
        className={validationErrors[field.key] ? 'ra-input-error' : ''}
        type={field.type}
        onWheel={field.type === 'number' ? (event) => event.currentTarget.blur() : undefined}
        name={field.key}
        value={form[field.key] ?? ''}
        onChange={handleChange}
        readOnly={Boolean(field.readOnly)}
        placeholder={field.type === 'number' ? '0.00' : `Enter ${field.label.toLowerCase()}...`}
      />
    );
  };

  const tableData = useMemo(() => ({
    columns: [
      { label: 'Sl.No', field: 'sl_no', width: 80 },
      { label: 'Status', field: 'record_status' },
      ...TABLE_ONLY_FIELDS_FIRST.map((field) => ({ label: field.label, field: field.key })),
      ...FIELDS.filter((field) => field.key !== 'internal_notes')
        .map((field) => ({ label: field.label, field: field.key })),
      ...TABLE_ONLY_FIELDS_LAST.map((field) => ({ label: field.label, field: field.key })),
      { label: 'Action', field: 'action', sort: false, width: 180 },
    ],
    rows: locationFilteredRows.map((row, index) => {
      const recordStatus = getRecordStatus(row);
      const locationKey = String(row.branchLocationCode || row.location_name || '').trim().toLowerCase();
      const location = locations.find((item) => (
        String(item?.location_name || '').trim().toLowerCase() === locationKey
        || String(item?.code || '').trim().toLowerCase() === locationKey
      ));
      const rowColor = location?.claim_row_color || '#ffffff';
      const mapped = {
        _rowSx: {
          bgcolor: rowColor,
          '&.MuiTableRow-hover:hover': { bgcolor: rowColor, filter: 'brightness(0.97)' },
        },
        sl_no: index + 1,
        record_status: recordStatus === 'Open'
          ? <span className="ra-status-open">Open</span>
          : <span className="ra-status-closed">Closed</span>,
        action: (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'nowrap' }}>
            {recordStatus === 'Open' && canEdit ? (
              <Button size="small" variant="outlined" className="ra-nowrap-btn" onClick={() => openCloseFormModal(row)}>
                Close
              </Button>
            ) : null}
            {canEdit ? (
              <Button size="small" variant="outlined" className="ra-nowrap-btn" onClick={() => openEditClosedModal(row)}>
                Edit
              </Button>
            ) : null}
            {canEditDocuments && !canEdit ? (
              <Button size="small" variant="outlined" className="ra-nowrap-btn" onClick={() => openEditClosedModal(row)}>Documents</Button>
            ) : null}
            {canEdit ? (
              <Button size="small" color="warning" variant="outlined" className="ra-nowrap-btn" onClick={() => handleMoveBackToProcessing(row)} disabled={String(movingBackId) === String(row._id)}>
                {String(movingBackId) === String(row._id) ? 'Moving...' : 'Move Back'}
              </Button>
            ) : null}
            <Button size="small" variant="text" className="ra-nowrap-btn" onClick={() => handleOpenHistory(row)}>
              Event Log
            </Button>
            {recordStatus === 'Open' && !canEdit ? '-' : null}
          </div>
        ),
      };

      TABLE_ONLY_FIELDS_FIRST.forEach((field) => {
        const fieldValue = field.key === 'location_name'
          ? row.branchLocationCode || row.location_name
          : field.key === 'loss_date'
            ? formatLossDate(row)
          : row[field.key];

        mapped[field.key] = field.key === 'insurance_number' && fieldValue
          ? (
            <button
              type="button"
              className="ra-link-button"
              onClick={() => handleOpenNotesModal(row)}
            >
              {fieldValue}
            </button>
          )
          : (fieldValue || '-');
      });

      FIELDS.forEach((field) => {
        const vehicle = getFirstVehicle(row);
        const fieldValue = field.key === 'ra_number'
          ? row.rentalAgreementNumber || row.ra_number
          : field.key === 'unit'
            ? vehicle.unitFleetNumber || row.unit
            : field.key === 'license_plate'
              ? vehicle.licensePlate || vehicle.plateNumber || row.license_plate || row.licensePlate || row.plateNumber
            : field.key === 'model_colour'
              ? row.model_colour || [vehicle.model, vehicle.color].filter(Boolean).join('*')
              : field.key === 'year_make'
                ? row.year_make || [vehicle.year, vehicle.make].filter(Boolean).join(' ')
                : field.key === 'vin'
                  ? vehicle.vin || row.vin
                  : row[field.key];

        mapped[field.key] = field.key === 'chargeback'
          ? row[field.key]
            ? <span className={row[field.key] === 'Yes' ? 'ra-badge-yes' : 'ra-badge-no'}>{row[field.key]}</span>
            : '-'
          : formatCell(field.key, fieldValue);
      });

      TABLE_ONLY_FIELDS_LAST.forEach((field) => {
        mapped[field.key] = field.key === 'created_datetime'
          ? formatDate(row[field.key])
          : (row[field.key] || '-');
      });

      return mapped;
    }),
  }), [canDelete, canEdit, canEditDocuments, locationFilteredRows, locations, openRecords]);

  if (!canView) {
    return <AccessDeniedState message="You do not have permission to view closed form records." />;
  }

  return (
    <>
      <Box sx={{ minHeight: '100vh', ml: { md: '240px' }, mt: '64px', p: { xs: 2, md: 4 } }}>
        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, mb: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }}>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.75 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#2454d6',
                    bgcolor: 'rgba(36, 84, 214, 0.08)',
                    border: '1px solid rgba(36, 84, 214, 0.14)',
                  }}
                >
                  <MdOutlineTableChart size={20} />
                </Box>
                <Typography variant="h4" color="text.primary" sx={{ fontWeight: 700, letterSpacing: 0 }}>
                  {title}
                </Typography>
              </Stack>
              <Typography color="text.secondary">
                {rows.length} total records
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              mt: 2.5,
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: '#f8fafc',
              border: '1px solid rgba(148, 163, 184, 0.24)',
            }}
          >
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', lg: 'flex-end' }}
              justifyContent="space-between"
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'flex-end' }}>
              <Box sx={{ minWidth: { xs: '100%', sm: 260 } }}>
                <Typography variant="caption" color="text.secondary">Location</Typography>
                <FormControl size="small" fullWidth sx={{ mt: 0.25 }}>
                  <Select value={selectedLocation} onChange={handleLocationChange}>
                    {locationOptions.map((locationName) => (
                      <MenuItem key={locationName} value={locationName}>
                        {locationName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ minWidth: { xs: '100%', sm: 170 } }}>
                <Typography variant="caption" color={dateRangeError ? 'error' : 'text.secondary'}>From Date</Typography>
                <OpenFormDatePicker
                  id="closed-forms-from-date"
                  name="fromDate"
                  value={fromDate}
                  onChange={(event) => setFromDate(event.target.value)}
                  max={toDate || undefined}
                  error={dateRangeError}
                />
              </Box>
              <Box sx={{ minWidth: { xs: '100%', sm: 170 } }}>
                <Typography variant="caption" color={dateRangeError ? 'error' : 'text.secondary'}>To Date</Typography>
                <OpenFormDatePicker
                  id="closed-forms-to-date"
                  name="toDate"
                  value={toDate}
                  onChange={(event) => setToDate(event.target.value)}
                  min={fromDate || undefined}
                  error={dateRangeError}
                />
              </Box>
              </Stack>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.25}
                alignItems={{ xs: 'stretch', sm: 'flex-end' }}
                sx={{ alignSelf: { lg: 'flex-end' } }}
              >
                <Button
                  variant="outlined"
                  startIcon={<IoDownloadOutline />}
                  className="ra-nowrap-btn ra-header-action-btn"
                  onClick={handleExportExcel}
                  disabled={locationFilteredRows.length === 0}
                  sx={{ minHeight: 44, px: 2.25 }}
                >
                  Export Excel
                </Button>

                {canEdit ? (
                  <Button
                    variant="contained"
                    startIcon={<IoCheckmarkDoneOutline />}
                    className="ra-nowrap-btn ra-header-action-btn wide"
                    onClick={() => openCloseFormModal()}
                    disabled={openRecords.length === 0}
                    sx={{ minHeight: 44, px: 2.25 }}
                  >
                    Close Existing Open Form
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          </Box>

          {error && <div className="ra-error" style={{ marginTop: 16 }}>{error}</div>}
          {dateRangeError && <div className="ra-error" style={{ marginTop: 16 }}>To Date should not be less than From Date.</div>}
          {successMessage && <div className="ra-success" style={{ marginTop: 16 }}>{successMessage}</div>}
        </Paper>

        <AppDatatable
          title="Insurance Records"
          subtitle="Search, sort, and paginate records directly from the table."
          data={tableData}
          loading={loading}
          noFoundMessage="No records found."
        />
      </Box>

      <Dialog
        open={showModal}
        onClose={resetFormState}
        fullWidth
        maxWidth="lg"
        sx={{
          '& input[type="number"]': {
            MozAppearance: 'textfield',
            appearance: 'textfield',
          },
          '& input[type="number"]::-webkit-inner-spin-button, & input[type="number"]::-webkit-outer-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 4,
            bgcolor: '#ececec',
            backgroundImage: 'none',
            p: { xs: 1, md: 2 },
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <div className="ra-modal-header">
            <div>
              <h3>{!canEdit ? 'Manage Documents' : editingClosedId ? 'Edit Closed Form' : 'Close Existing Form'}</h3>
              <p className="ra-modal-subtitle">
                {editingClosedId ? 'Update Claims Processing and Close Existing Form fields.' : 'Select an open form and enter the remaining closing details.'}
              </p>
            </div>
            <IconButton onClick={resetFormState} color="inherit">
              <IoCloseOutline />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers={false}>
          {error && <div className="ra-error">{error}</div>}

          <div className={`ra-form-shell${canEdit ? '' : ' document-only'}`}>
            <div className="ra-form-sidecard">
              <span className="ra-sidecard-step">{editingClosedId ? 'Edit Form' : 'Close Form'}</span>
              <h4>{editingClosedId ? 'Update Finalized Record' : 'Finish Open Form'}</h4>
              <p>
                Choose an open record, review the vehicle details, then save the financial and payment information to close it.
              </p>
            </div>

            <div className="ra-form-main">
              <div className="ra-form-group-title">Location</div>
              <div className="ra-form-grid full">
                <div className="ra-field">
                  <label>
                    Select Location
                    <span style={{ color: '#ff6b6b' }}> *</span>
                  </label>
                  <select
                    className={validationErrors.location_name ? 'ra-input-error' : ''}
                    name="location_name"
                    value={form.location_name ?? ''}
                    onChange={handleChange}
                    disabled={Boolean(selectedCloseRecordId)}
                  >
                    <option value="">- Choose a location -</option>
                    {locations.map((loc) => (
                      <option key={loc.location_name} value={loc.location_name}>
                        {loc.location_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {!editingClosedId && <><div className="ra-form-group-title">Select Open Form</div>
              <div className="ra-form-grid full">
                <div className="ra-field">
                  <label>Select Open Form</label>
                  <select value={selectedCloseRecordId} onChange={handleCloseRecordSelection}>
                    <option value="">- Select an open form -</option>
                    {openRecords.map((row) => (
                      <option key={row._id} value={row._id}>
                        {(row.rentalAgreementNumber || row.ra_number || 'No R/A')} | {getFirstVehicle(row).unitFleetNumber || row.unit || 'No Unit'} | {getFirstVehicle(row).vin || row.vin || 'No VIN'}
                      </option>
                    ))}
                  </select>
                </div>
              </div></>}

              {selectedCloseRecordId && (
                <>
                  <div className="ra-form-group-title">Opened Details</div>
                  <div className="ra-summary-grid">
                    <div className="ra-summary-card">
                      <span>R/A Number</span>
                      <strong>{form.ra_number || '-'}</strong>
                    </div>
                    <div className="ra-summary-card">
                      <span>Unit</span>
                      <strong>{form.unit || '-'}</strong>
                    </div>
                    <div className="ra-summary-card">
                      <span>VIN</span>
                      <strong>{form.vin || '-'}</strong>
                    </div>
                    <div className="ra-summary-card">
                      <span>Model & Colour</span>
                      <strong>{form.model_colour || '-'}</strong>
                    </div>
                    <div className="ra-summary-card">
                      <span>Year & Make</span>
                      <strong>{form.year_make || '-'}</strong>
                    </div>
                    <div className="ra-summary-card">
                      <span>Insurance Number</span>
                      <strong>{form.insurance_number || '-'}</strong>
                    </div>
                  </div>
                </>
              )}

              {editingClosedId && (
                <div>
                  <div className="ra-form-group-title">Claims Processing — Daily Work / Internal Comments</div>
                  {canEdit && (
                    <Box sx={{ mb: 2 }}>
                      <Typography sx={{ mb: 1, fontWeight: 600 }}>Add new comment</Typography>
                      <RichTextEditor
                        value={form.new_internal_note_entry?.note || ''}
                        onChange={(note) => setForm((prev) => ({ ...prev, new_internal_note_entry: { note } }))}
                        placeholder="Add internal comments and daily work updates?"
                      />
                      <Typography sx={{ mt: 1, color: 'text.secondary', fontSize: 12 }}>
                        Your new comment will be added when you click Save Changes.
                      </Typography>
                    </Box>
                  )}
                  {getClaimsProcessComments(form).length ? (
                    <Stack spacing={1.25}>
                      {getClaimsProcessComments(form).map((entry, index) => (
                        <Box
                          key={entry?.id || entry?._id || `process-comment-${index}`}
                          sx={{ p: 1.5, border: '1px solid rgba(148, 163, 184, 0.35)', borderRadius: 2, bgcolor: '#fff' }}
                        >
                          <Box sx={{ overflowWrap: 'anywhere' }}>
                            <RichTextContent value={entry?.note || entry?.notes} />
                          </Box>
                          <Typography sx={{ mt: 0.75, color: 'text.secondary', fontSize: 12 }}>
                            {[entry?.created_by, entry?.created_datetime
                              ? new Date(entry.created_datetime).toLocaleString()
                              : ''].filter(Boolean).join(' • ') || 'Claim processing note'}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography sx={{ p: 1.5, color: 'text.secondary', bgcolor: '#fff', borderRadius: 2 }}>
                      No Daily Work / Internal Comments were added during claims processing.
                    </Typography>
                  )}
                </div>
              )}

              {editingClosedId && PROCESSING_GROUPS.map((group) => (
                <div key={group.title}>
                  <div className="ra-form-group-title">Claims Processing — {group.title}</div>
                  <div className="ra-form-grid">
                    {group.fields.map((field) => (
                      <div className="ra-field" key={field.key}>
                        <label>{field.label}</label>
                        {renderField(field)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {editingClosedId && (
                <div>
                  <div className="ra-form-group-title">Claims Processing — Demand Package</div>
                  <div className="claim-process-checklist">
                    {DEMAND_PACKAGE_ITEMS.map(([key, label]) => (
                      <label key={key} className="claim-process-check-row">
                        <input
                          type="checkbox"
                          checked={Boolean(form.demand_package?.[key])}
                          onChange={() => handleDemandPackageChange(key)}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {editingClosedId && <div className="ra-form-group-title">Close Existing Form Fields</div>}
              {formGroups.map((group) => (
                <div key={group.title}>
                  <div className="ra-form-group-title">{group.title}</div>
                  <div className={`ra-form-grid ${group.fields.some((field) => field.type === 'textarea') ? 'full' : ''}`}>
                    {group.fields.map((field) => (
                      <div
                        className="ra-field"
                        key={field.key}
                        style={field.type === 'textarea' ? { gridColumn: '1 / -1' } : {}}
                      >
                        <label>{field.label}</label>
                        {renderField(field)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {selectedCloseRecordId && (
                <div className="claim-documents-section">
                  <div className="ra-form-group-title">Uploaded Documents</div>
                  <Typography sx={{ mb: 1.25, color: '#9a6700', fontSize: 13, fontWeight: 700 }}>
                    Note: Uploading or deleting a document is saved immediately. You do not need to save the form.
                  </Typography>
                  <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.25 }}>
                    {canEditDocuments ? <Button variant="contained" component="label" disabled={documentsUpdating}>
                      {documentsUpdating ? 'Updating...' : 'Upload New Documents'}
                      <input
                        hidden
                        type="file"
                        multiple
                        onChange={(event) => {
                          const files = event.target.files;
                          if (files?.length) updateClosedFormDocuments(form.uploaded_files || [], files);
                          event.target.value = '';
                        }}
                      />
                    </Button> : null}
                  </Stack>
                  {Array.isArray(form.uploaded_files) && form.uploaded_files.length > 0 ? (
                    <Stack spacing={1.25} sx={{ mb: 2 }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                        <Button variant="outlined" onClick={() => viewAllUploadedFiles(baseURL, form.uploaded_files)}>
                          View All
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => window.open(`${baseURL}/claim-uploads/${uploadWorkflowCategory}/${selectedCloseRecordId}/download-all`, '_blank', 'noopener,noreferrer')}
                        >
                          Download All as ZIP
                        </Button>
                      </Stack>
                      {form.uploaded_files.map((file, index) => (
                        <Stack
                          key={`${file?.saved_name || file?.original_name || 'document'}-${index}`}
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ sm: 'center' }}
                          justifyContent="space-between"
                          sx={{ p: 1.25, border: '1px solid rgba(148, 163, 184, 0.3)', borderRadius: 2, bgcolor: '#fff' }}
                        >
                          <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                            {file?.original_name || file?.saved_name || `Document ${index + 1}`}
                          </Typography>
                          <Stack direction="row" spacing={1} flexShrink={0}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => window.open(buildUploadedFileUrl(baseURL, file), '_blank', 'noopener,noreferrer')}
                              disabled={!buildUploadedFileUrl(baseURL, file)}
                            >
                              View
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => window.open(buildUploadedFileUrl(baseURL, file, true), '_blank', 'noopener,noreferrer')}
                              disabled={!buildUploadedFileUrl(baseURL, file, true)}
                            >
                              Download
                            </Button>
                            {canEditDocuments ? <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => handleDeleteClosedFormDocument(file)}
                              disabled={documentsUpdating}
                            >
                              Delete
                            </Button> : null}
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>
                  ) : (
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                      No documents were uploaded with this claim.
                    </Typography>
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button variant="outlined" color="inherit" onClick={resetFormState}>
            Cancel
          </Button>
          {canEdit ? <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : editingClosedId ? 'Save Changes' : 'Save & Close'}
          </Button> : null}
        </DialogActions>
      </Dialog>

      <Dialog open={showHistoryModal} onClose={handleCloseHistory} fullWidth maxWidth="lg">
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ md: 'center' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>Complete Event Log</Typography>
              <Typography color="text.secondary">
                {historyRecord?.insurance_number ? `Insurance ${historyRecord.insurance_number}` : 'Complete record lifecycle'}
              </Typography>
            </Box>
            <Chip label={`Record ID: ${historyRecord?._id || '-'}`} color="primary" variant="outlined" />
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#f6f9fc' }}>
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#dce9ff' }}>
                    {['Event', 'Date', 'Employee', 'Changed Fields', 'Action'].map((label) => (
                      <TableCell key={label} sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>{label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyLoading ? (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>Loading complete event log...</TableCell></TableRow>
                  ) : historyRows.length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>{historyError || 'No event logs found.'}</TableCell></TableRow>
                  ) : historyRows.map((log, index) => {
                    const changedFields = changedHistoryFields(log);
                    return (
                      <TableRow key={`${log._id}-${log.action}-${index}`} hover>
                        <TableCell>{historyActionLabel(log.action)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(log.event_datetime)}</TableCell>
                        <TableCell>{log.actor || '-'}</TableCell>
                        <TableCell>{changedFields.length ? changedFields.join(', ') : '-'}</TableCell>
                        <TableCell><Button size="small" variant="outlined" onClick={() => setSelectedHistoryLog(log)}>View</Button></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </DialogContent>
        <DialogActions><Button variant="outlined" onClick={handleCloseHistory}>Close</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(selectedHistoryLog)} onClose={() => setSelectedHistoryLog(null)} fullWidth maxWidth="lg">
        <DialogTitle>Event Details</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {[
              ['Before', selectedHistoryLog?.before_data],
              ['After', selectedHistoryLog?.after_data],
            ].map(([title, data]) => (
              <Grid item xs={12} md={6} key={title}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>{title}</Typography>
                  {data && Object.keys(data).length ? Object.entries(data).map(([key, value]) => (
                    <Box key={`${title}-${key}`} sx={{ mb: 1.25 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>{key.replaceAll('_', ' ')}</Typography>
                      <Typography component="pre" sx={{ m: 0, fontFamily: 'inherit', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {formatHistoryValue(value)}
                      </Typography>
                    </Box>
                  )) : <Typography color="text.secondary">No data.</Typography>}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions><Button variant="outlined" onClick={() => setSelectedHistoryLog(null)}>Close</Button></DialogActions>
      </Dialog>

      <Dialog
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 1,
            textAlign: 'center',
          },
        }}
      >
        <DialogTitle sx={{ fontSize: 20, fontWeight: 700, pb: 1 }}>Delete Record?</DialogTitle>
        <DialogContent>
          <p style={{ margin: 0, color: '#111827', lineHeight: 1.6 }}>
            This action cannot be undone. Are you sure you want to permanently delete this record?
          </p>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2.5 }}>
          <Button variant="outlined" color="inherit" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showNotesModal}
        onClose={resetNotesState}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 4,
            bgcolor: '#ececec',
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <div className="ra-modal-header">
            <h3 style={{ margin: 0 }}>Insurance Notes</h3>
            <IconButton onClick={resetNotesState} color="inherit">
              <IoCloseOutline />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          {notesError && <div className="ra-error">{notesError}</div>}

          <div className="ra-notes-meta">
            <div>
              <span>Insurance Number</span>
              <strong>{selectedNoteRow?.insurance_number || '-'}</strong>
            </div>
            <div>
              <span>R/A Number</span>
              <strong>{selectedNoteRow?.ra_number || '-'}</strong>
            </div>
          </div>

          <div className="ra-field">
            <label>Notes</label>
            <textarea
              className="ra-notes-textarea"
              value={notesValue}
              onChange={(e) => setNotesValue(e.target.value)}
              placeholder={notesLoading ? 'Loading notes...' : 'Enter notes'}
              disabled={notesLoading}
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button variant="outlined" color="inherit" onClick={resetNotesState}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmitNotes} disabled={notesSubmitting || notesLoading || !canEdit}>
            {notesSubmitting ? 'Saving...' : 'Submit Notes'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
